/**
 * Quiz diagnóstico para adultos 50+ — identifica los padecimientos
 * más frecuentes de quiropráctica y terapia física a esa edad.
 * URL: /quiz/50-mas (no visible en menú)
 */

export const QUIZ50_META = {
  id: "50-mas-evaluacion",
  title: "Evaluación de salud articular y postural",
  intro:
    "A partir de los 50, el cuerpo cambia. Articulaciones, nervios y postura piden otro tipo de cuidado. " +
    "10 preguntas honestas para detectar los 10 padecimientos más comunes que tratamos a esta edad.",
  therapist: {
    name: "Fisioterapeuta Omar Aguilera",
    role: "Fisioterapeuta y Quiropráctico",
    service: "terapia_fisica",
    phoneWa: "527712141634",
    photo: "/img/team/therapist_1_quiro_fisio.png",
  },
};

export const QUIZ50_QUESTIONS = [
  { id: "q1", text: "Te cuesta más trabajo subir o bajar escaleras que hace 5 años, o tienes que apoyarte del barandal.", flags: ["artrosis_rodilla"] },
  { id: "q2", text: "Tu rodilla truena, se hincha o duele tras estar mucho tiempo sentado/a o al iniciar la marcha.", flags: ["artrosis_rodilla", "condromalacia"] },
  { id: "q3", text: "Sientes dolor en la cadera o ingle al caminar distancias largas, o al ponerte de pie tras estar sentado/a.", flags: ["artrosis_cadera", "bursitis_cadera"] },
  { id: "q4", text: "Tienes dificultad o dolor al levantar el brazo arriba de la cabeza (peinarte, alcanzar un estante, abrocharte la espalda).", flags: ["hombro_congelado", "tendinitis_hombro"] },
  { id: "q5", text: "Al caminar distancias largas sientes pesadez, hormigueo o dolor en las piernas que mejora al sentarte o inclinarte hacia adelante.", flags: ["estenosis_lumbar"] },
  { id: "q6", text: "Tienes dolor que se irradia desde la espalda baja hacia un glúteo, muslo o pierna (a veces con hormigueo).", flags: ["hernia_lumbar", "ciatica"] },
  { id: "q7", text: "Sufres dolor crónico de cuello, dolores de cabeza tensionales recurrentes o mareos sin causa aparente.", flags: ["cervicalgia", "vertigo_cervical"] },
  { id: "q8", text: "Sientes hormigueo, adormecimiento o pérdida de fuerza en los dedos de las manos, especialmente al despertar o por la noche.", flags: ["tunel_carpiano"] },
  { id: "q9", text: "Has tenido alguna caída en los últimos 12 meses, o sensación de inestabilidad al caminar o pararte de la cama.", flags: ["inestabilidad"] },
  { id: "q10", text: "Has notado pérdida de fuerza en piernas o brazos para hacer cosas que antes hacías sin problema (cargar bolsas, levantarte del piso).", flags: ["sarcopenia", "estenosis_lumbar"] },
];

export const QUIZ50_CONDITIONS = {
  artrosis_rodilla: {
    name: "Artrosis de rodilla (gonartrosis)",
    short: "Artrosis de rodilla",
    description:
      "Desgaste progresivo del cartílago articular de la rodilla. Muy frecuente a partir de los 50 — afecta hasta al 60% de las personas. Genera dolor, rigidez matinal y crepitación.",
    treatment:
      "Fortalecimiento del cuádriceps + magnetoterapia + ácido hialurónico (si aplica) + reeducación de la marcha. Reduce el dolor significativamente en 6-8 semanas.",
    severity: "mid",
  },
  artrosis_cadera: {
    name: "Artrosis de cadera (coxartrosis)",
    short: "Artrosis de cadera",
    description:
      "Desgaste de la articulación coxofemoral. Provoca dolor inguinal, limitación al cruzar la pierna o ponerse los calcetines, y cojera progresiva.",
    treatment:
      "Movilización articular + estiramiento de rotadores + fortalecimiento de glúteos + reeducación postural. Excelente respuesta conservadora.",
    severity: "mid",
  },
  hombro_congelado: {
    name: "Hombro congelado (capsulitis adhesiva)",
    short: "Hombro congelado",
    description:
      "Inflamación y rigidez progresiva de la cápsula articular del hombro. Pico de incidencia entre 50-60 años, más común en mujeres y personas con diabetes.",
    treatment:
      "Movilización capsular escalonada + ondas de choque + ejercicio terapéutico activo. Plan de 8-12 semanas con recuperación funcional casi completa.",
    severity: "high",
  },
  tendinitis_hombro: {
    name: "Tendinitis del manguito rotador",
    short: "Tendinitis de hombro",
    description:
      "Inflamación o desgaste de los tendones que estabilizan el hombro. Causa dolor al elevar el brazo y al dormir del lado afectado.",
    treatment:
      "Ultrasonido terapéutico + ejercicios excéntricos + reeducación del movimiento. Si hay desgarro pequeño, terapia conservadora resuelve el 80%.",
    severity: "mid",
  },
  estenosis_lumbar: {
    name: "Estenosis del canal lumbar",
    short: "Estenosis lumbar",
    description:
      "Estrechamiento del canal medular que comprime las raíces nerviosas. El síntoma clásico es 'claudicación neurógena': dolor de piernas al caminar que mejora al sentarse o inclinarse.",
    treatment:
      "Decoaptación lumbar + ejercicios de flexión + fortalecimiento profundo + reeducación postural. Mejora notable en 80% de los casos sin cirugía.",
    severity: "high",
  },
  hernia_lumbar: {
    name: "Hernia discal lumbar",
    short: "Hernia discal lumbar",
    description:
      "Protrusión del disco intervertebral que comprime la raíz nerviosa. Genera dolor irradiado, hormigueo o debilidad en una pierna.",
    treatment:
      "El 90% de las hernias responden a terapia conservadora: tracción + McKenzie + reeducación del core. Solo el 5-10% requiere cirugía.",
    severity: "high",
  },
  ciatica: {
    name: "Lumbociática crónica",
    short: "Ciática",
    description:
      "Irritación crónica del nervio ciático por compresión, contractura del piramidal o desbalance postural. Muy común y muy tratable.",
    treatment:
      "Manipulación quiropráctica suave + estiramiento neural + fortalecimiento profundo. Atender pronto evita cronificación.",
    severity: "high",
  },
  cervicalgia: {
    name: "Cervicalgia crónica",
    short: "Cervicalgia crónica",
    description:
      "Dolor y rigidez persistente en cuello/trapecio. Puede generar dolores de cabeza tensionales y limitar el giro de la cabeza al manejar.",
    treatment:
      "Masaje terapéutico profundo + movilización cervical suave + ejercicios estabilizadores. Excelente respuesta en 4-6 sesiones.",
    severity: "mid",
  },
  vertigo_cervical: {
    name: "Vértigo cervicogénico",
    short: "Vértigo cervical",
    description:
      "Sensación de mareo o desequilibrio relacionado con disfunción de las articulaciones cervicales superiores. NO es el vértigo del oído.",
    treatment:
      "Movilización cervical específica + ejercicios oculomotores + reeducación propioceptiva. La mayoría mejora en 4-8 sesiones.",
    severity: "mid",
  },
  tunel_carpiano: {
    name: "Síndrome del túnel carpiano",
    short: "Túnel carpiano",
    description:
      "Compresión del nervio mediano en la muñeca. Provoca hormigueo nocturno, pérdida de fuerza al agarrar y torpeza en los dedos pulgar e índice.",
    treatment:
      "Terapia conservadora (movilización del nervio mediano + ejercicios + férula nocturna) resuelve hasta el 70% de los casos leves-moderados.",
    severity: "mid",
  },
  inestabilidad: {
    name: "Inestabilidad y riesgo de caídas",
    short: "Inestabilidad postural",
    description:
      "Pérdida de equilibrio relacionada con sarcopenia (pérdida muscular), debilidad de tobillo/cadera o disfunción vestibular. Aumenta el riesgo de fractura.",
    treatment:
      "Programa de propiocepción + fortalecimiento de glúteo medio + entrenamiento de equilibrio dinámico. Reduce 50-70% el riesgo de caídas.",
    severity: "high",
  },
  sarcopenia: {
    name: "Sarcopenia (pérdida de fuerza muscular)",
    short: "Pérdida de fuerza",
    description:
      "Disminución progresiva de masa y fuerza muscular relacionada con la edad. Acelera el envejecimiento funcional y aumenta el riesgo de lesiones.",
    treatment:
      "Entrenamiento de fuerza progresivo supervisado + nutrición proteica + estilo de vida activo. Resultados visibles en 8-12 semanas.",
    severity: "mid",
  },
  bursitis_cadera: {
    name: "Bursitis trocantérea",
    short: "Bursitis de cadera",
    description:
      "Inflamación de la bursa lateral de la cadera. Provoca dolor al acostarse del lado afectado o al subir escaleras.",
    treatment:
      "Fortalecimiento del glúteo medio + crioterapia + corrección de marcha. Excelente pronóstico con tratamiento dirigido.",
    severity: "mid",
  },
  condromalacia: {
    name: "Condromalacia rotuliana",
    short: "Rodilla anterior",
    description:
      "Reblandecimiento del cartílago detrás de la rótula. Provoca dolor al subir escaleras, al ponerse en cuclillas o tras estar mucho tiempo sentado.",
    treatment:
      "Fortalecimiento del vasto interno + reeducación de la pisada + vendaje neuromuscular. Reducción de impacto temporal.",
    severity: "mid",
  },
};

export function scoreConditions50(answers) {
  const counts = {};
  QUIZ50_QUESTIONS.forEach((q, i) => {
    if (!answers[i]) return;
    q.flags.forEach((f) => { counts[f] = (counts[f] || 0) + 1; });
  });
  return Object.entries(counts)
    .map(([key, score]) => ({ key, score, ...QUIZ50_CONDITIONS[key] }))
    .sort((a, b) => b.score - a.score);
}

export function overallSeverity50(ranked) {
  if (ranked.some((c) => c.severity === "high")) return "red";
  if (ranked.length >= 3) return "yellow";
  if (ranked.length >= 1) return "yellow";
  return "green";
}
